import DataLabelRow from '@lyra/core/components/DataLabelRow'
import Section from '@lyra/core/components/Section'
import SpanText from '@lyra/core/components/SpanText'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatUSD from '@lyra/core/utils/formatUSD'
import { InstrumentType, Ticker } from '@lyra/web/constants/instruments'
import { OrderQuote, OrderQuoteParams, OrderType } from '@lyra/web/constants/order'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { formatInstrument, parseInstrumentName } from '@lyra/web/utils/instruments'
import { countDecimals } from '@lyra/web/utils/number'
import { useMemo } from 'react'

type Props = {
  ticker: Ticker
  quoteParams: OrderQuoteParams
  quote?: OrderQuote | null
  isLoading?: boolean
  showMarkPrice?: boolean
}

const TradeFormTicket = ({ ticker, quoteParams, quote, isLoading, showMarkPrice }: Props) => {
  const instrumentName = ticker.instrument_name

  const isLimitOrder = quoteParams.order_type === OrderType.Limit

  const isBuy = quoteParams.direction === 'buy'

  const limitPrice = +quoteParams.limit_price
  const amount = +quoteParams.amount

  // Fee is accounted for in quote
  const marginRequired = quote ? +quote.pre_initial_margin - +quote.post_initial_margin : 0
  const fees = quote ? +quote.estimated_fee : 0
  const minReceivedOrMaxCost = quote ? limitPrice * amount + fees * (isBuy ? 1 : -1) : 0

  const instrument = parseInstrumentName(instrumentName)

  const { subaccount, buyingPower } = useSubaccount()

  const isOption = instrument?.type === InstrumentType.Options
  const isPerp = instrument?.type === InstrumentType.Perps
  const amountStep = +ticker.amount_step

  const openPosition = useMemo(
    () =>
      subaccount?.positions
        .filter((p) => !!+p.amount)
        .find((position) => position.instrument_name === instrumentName),
    [subaccount, instrumentName]
  )

  const amountDir = +quoteParams.amount * (isBuy ? 1 : -1)
  const isClosingPosition = openPosition ? +openPosition.amount + amountDir === 0 : false

  return (
    <Section.YStack minHeight={100}>
      {showMarkPrice ? (
        <DataLabelRow
          label={`${formatInstrument(instrumentName)} Price`}
          value={formatUSD(+ticker.mark_price, { maxDps: 18 })}
        />
      ) : null}
      {openPosition ? (
        <DataLabelRow
          width="100%"
          label="Current Position"
          value={
            <SpanText textDecorationLine={isClosingPosition ? 'line-through' : undefined}>
              {+openPosition.amount >= 0 ? 'Long' : 'Short'}{' '}
              {formatNumber(Math.abs(+openPosition.amount), { dps: countDecimals(amountStep) })}
              {isPerp ? ` ${ticker.base_currency}` : ''}
            </SpanText>
          }
        />
      ) : null}
      {isLimitOrder && isOption ? (
        <DataLabelRow
          label={!isBuy ? 'Min Received' : 'Max Cost'}
          // for orderbook, cost is always defined - so don't show ... even if isLoading
          value={isLoading ? '...' : formatUSD(Math.max(0, minReceivedOrMaxCost))}
        />
      ) : null}
      <DataLabelRow
        label={marginRequired >= 0 ? 'Margin Required' : 'Margin Unlocked'}
        value={isLoading ? '...' : formatUSD(Math.abs(marginRequired))}
      />
      <DataLabelRow
        label="Buying Power"
        color={quote && !quote.is_valid ? 'red' : 'primary'}
        value={formatUSD(buyingPower)}
      />
    </Section.YStack>
  )
}

export default TradeFormTicket
